import React, { useState } from "react";
import { buildNavTree } from "./manualIndex";

// - - - - - - - - - -
// ManualNav — left-side table of contents for the in-app manual.
//
// Renders the page list from manualIndex as a vertical button list,
// with one twist: pages whose slug contains "/" are grouped under an
// expandable parent (e.g. all "time-travel/..." slugs collapse under
// a "Time Travel" parent). The grouping is derived from the slug
// shape via the pure buildNavTree helper — manualIndex itself stays
// flat, so adding a new group is just "add entries with a new slug
// prefix and an entry in GROUP_LABELS."
//
// Group nodes start expanded by default so a new user sees the full
// scope of the manual on first open. Click the parent to collapse.
// Selection state lives in the parent component (UserManual) and is
// passed down so the active page can be highlighted regardless of
// whether it's at the top level or nested under a group.

function ManualNav({ pages, selectedSlug, onSelect }) {
  const tree = buildNavTree(pages);
  const [expandedGroups, setExpandedGroups] = useState(() => {
    const set = new Set();
    for (const node of tree) {
      if (node.type === "group") {
        set.add(node.slug);
      }
    }
    return set;
  });

  if (tree.length === 0) {
    return (
      <nav className="w-56 shrink-0 pr-3 border-r border-skin-base">
        <div className="opacity-70 text-sm">No manual pages available.</div>
      </nav>
    );
  }

  const toggleGroup = (groupSlug) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupSlug)) {
        next.delete(groupSlug);
      } else {
        next.add(groupSlug);
      }
      return next;
    });
  };

  return (
    <nav
      data-testid="manual-nav"
      className="w-56 shrink-0 pr-3 border-r border-skin-base"
    >
      <ul className="flex flex-col gap-1">
        {tree.map((node) => {
          if (node.type === "page") {
            return (
              <li key={node.page.slug}>
                <PageButton
                  page={node.page}
                  isActive={node.page.slug === selectedSlug}
                  onSelect={onSelect}
                />
              </li>
            );
          }

          // Group node — expandable parent + nested page list.
          const isExpanded = expandedGroups.has(node.slug);
          return (
            <li key={`group:${node.slug}`}>
              <button
                type="button"
                onClick={() => toggleGroup(node.slug)}
                aria-expanded={isExpanded}
                data-testid={`manual-nav-group-${node.slug}`}
                className="w-full text-left px-2 py-1 rounded text-sm font-medium hover:underline flex items-center gap-1"
              >
                <span aria-hidden="true">{isExpanded ? "▾" : "▸"}</span>
                <span>{node.label}</span>
              </button>
              {isExpanded && (
                <ul className="flex flex-col gap-1 pl-4 mt-1">
                  {node.pages.map((page) => (
                    <li key={page.slug}>
                      <PageButton
                        page={page}
                        isActive={page.slug === selectedSlug}
                        onSelect={onSelect}
                      />
                    </li>
                  ))}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

// - - - - - - - - - -
// PageButton — single nav row, used at both top level and nested
// inside a group. Kept inline because it's only used here and adds
// clarity; if a third caller appears later, promote to its own file.

const PageButton = ({ page, isActive, onSelect }) => (
  <button
    type="button"
    onClick={() => onSelect(page.slug)}
    aria-current={isActive ? "page" : undefined}
    className={`w-full text-left px-2 py-1 rounded text-sm hover:underline ${
      isActive ? "font-bold text-skin-link" : ""
    }`}
  >
    {page.title}
  </button>
);

export default ManualNav;
